<template>
  <custom-modal
    class="custom-modal__extended"
    id="incident-report-modal"
    ref="incidentReportModalRef"
    v-b-modal.modal-center
    :title="modalTitle"
    :hide-backdrop="hideModalBackdrop"
    @accept="() => {}"
    @hide="onHideHandler"
  >
    <div id="screenshot-container" class="font-14 border-bottom pb-4">
      <div ref="highlight" class="highlight"></div>
      <validation-observer
        v-if="isFormStep"
        ref="formRef"
        v-slot="{}"
        tag="form"
      >
        <validation-provider rules="required" v-slot="{ errors }">
          <div class="color-primary-4 font-14 font-weight-bold mb-1">
            {{ $t('IssueDescription') }}:
          </div>
          <custom-text-area
            show-char-count
            v-model="model.issueDescription"
            :maxlength="500"
            :placeholder="$t('WriteComment')"
            :count-classes="['d-block text-right color-gray-6']"
            :error="errors[0]"
            :custom-error-msg="`${$t('RequiredField')}.`"
          />
        </validation-provider>
        <div class="mt-3">
          <div class="color-primary-4 font-14 font-weight-bold mb-1">
            {{ $t('IssueFrequency') }}:
          </div>
          <b-form-group v-slot="{ ariaDescribedby }">
            <validation-provider rules="required" v-slot="{ errors }">
              <b-form-radio
                v-model="model.issueFrequency"
                :aria-describedby="ariaDescribedby"
                name="frequency-radio"
                :value="item.id"
                v-for="(item, index) in frequencyOptions"
                :key="`frequency-item-${index}`"
                class="mb-2"
              >
                {{ item.name }}
              </b-form-radio>
              <custom-error
                v-if="errors[0]"
                class="no-border mt-2"
                :message="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </b-form-group>
        </div>
        <validation-provider rules="required" v-slot="{ errors }">
          <div class="color-primary-4 font-14 font-weight-bold mb-1">
            {{ $t('IssueComments') }}:
          </div>
          <custom-text-area
            show-char-count
            v-model="model.issueComments"
            :maxlength="500"
            :placeholder="$t('WriteComment')"
            :count-classes="['d-block text-right color-gray-6']"
            :error="errors[0]"
            :custom-error-msg="`${$t('RequiredField')}.`"
          />
        </validation-provider>
      </validation-observer>
      <div v-if="isScreenCaptureStep" class="text-center">
        <div class="color-black-2 font-14">
          {{ $t('SelectToHighlightOrDarkenImportantInfo') }}
        </div>
        <div class="text-center mt-3">
          <action-button
            class="btn-success-outline px-4 py-2 mr-3 btn-highlight"
            :text="$t('FeedbackHighlightLabel')"
            @click="onSelectHandler"
          />
        </div>
      </div>
      <div v-if="isConfirmFormStep">
        <div>
          <div class="color-gray-18 font-14 mb-1">
            {{ $t('IssueDescription') }}:
          </div>
          <div class="color-black font-14">
            {{ model.issueDescription }}
          </div>
        </div>
        <div class="mt-4">
          <div class="color-gray-18 font-14 mb-1">
            {{ $t('IncidentFrequency') }}:
          </div>
          <div class="color-black font-14">
            {{ model.issueFrequency }}
          </div>
        </div>
        <div class="mt-4">
          <div class="color-gray-18 font-14 mb-1">
            {{ $t('IssueComments') }}:
          </div>
          <div class="color-black font-14">
            {{ model.issueComments }}
          </div>
        </div>
        <div class="mt-4">
          <span class="color-gray-18 font-14 mb-1">
            {{ $t('FeedbackreviewLabel') }}:
          </span>
          <div class="p-3 rounded bg-secondary-11 text-center mt-2">
            <img
              v-if="model.image"
              :src="model.image"
              alt="image source"
              height="300px"
              width="100%"
            />
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="d-flex align-items-center justify-content-center pb-3">
        <action-button
          v-if="isFormStep"
          class="btn-success px-4 py-2"
          :text="$t('btnContinue.Text')"
          iright="fa fa-arrow-circle-right"
          @click="onContinueHandler"
        />
        <action-button
          v-if="isScreenCaptureStep"
          class="btn-success px-4 py-2"
          :text="$t('CaptureScreen')"
          @click="onCaptureScreenHandler"
        />
        <action-button
          v-if="isConfirmFormStep"
          class="btn-success px-4 py-2"
          :text="$t('survey-modal-send-text')"
          i="fas fa-arrow-to-right"
          @click="onSendHandler"
        />
      </div>
    </template>
  </custom-modal>
</template>

<script>
//libs
import { i18n } from '@/i18n';
import html2canvas from 'html2canvas';
import CustomTextArea from '@/components/common/CustomTextArea.vue';
import ActionButton from 'ActionButton';
import CustomError from 'custom-error';

//common components
import { CustomModal } from '@/components/common';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';

//services
import { sendIncidentReport as _sendIncidentReport } from '@/services/app.api.js';

//constants
const STEPS = {
  FILL_FORM: 1,
  SCREEN_CAPTURE: 2,
  CONFIRM_INFO: 3,
};

export default {
  name: 'IncidentReportModal',
  mixins: [alertMixin],
  components: {
    CustomModal,
    CustomTextArea,
    ActionButton,
    CustomError,
  },
  data: () => ({
    hideModalBackdrop: false,
    stepId: STEPS.FILL_FORM,
    model: {
      issueDescription: null,
      issueFrequency: null,
      issueComments: null,
      image: null,
    },
    frequencyOptions: [
      {
        id: i18n.t('TooFrequent'),
        name: i18n.t('TooFrequent'),
      },
      {
        id: i18n.t('Sometimes'),
        name: i18n.t('Sometimes'),
      },
      {
        id: i18n.t('Rarely'),
        name: i18n.t('Rarely'),
      },
      {
        id: i18n.t('Firsttime'),
        name: i18n.t('Firsttime'),
      },
    ],
  }),
  mounted() {
    this.$root.$on('capture-selected', (data) => {
      if (data) {
        this.model.image = data;
        this.viewCaptureScreenshot(false);
        this.showModal();
        this.hideModalBackdrop = false;
        this.stepId = STEPS.CONFIRM_INFO;
      } else {
        this.model.image = null;
        this.showModal();
      }
    });
  },
  computed: {
    modalTitle() {
      return {
        [STEPS.FILL_FORM]: i18n.t('Feedbackheader').toUpperCase(),
        [STEPS.SCREEN_CAPTURE]: i18n.t('FeedbackreviewLabel').toUpperCase(),
        [STEPS.CONFIRM_INFO]: i18n.t('Feedbackheader').toUpperCase(),
      }[this.stepId];
    },
    isFormStep() {
      return STEPS.FILL_FORM === this.stepId;
    },
    isScreenCaptureStep() {
      return STEPS.SCREEN_CAPTURE === this.stepId;
    },
    isConfirmFormStep() {
      return STEPS.CONFIRM_INFO === this.stepId;
    },
  },
  methods: {
    showModal() {
      this.$refs.incidentReportModalRef.showModal();
    },
    hideModal() {
      this.$refs.incidentReportModalRef.hideModal();
    },
    viewCaptureScreenshot(visible) {
      this.$refs.incidentReportModalRef.$parent.$parent.$refs.screenshotSelectionRef.show =
        visible;
    },
    onSelectHandler() {
      this.hideModal();
      this.viewCaptureScreenshot(true);
    },
    captureScreen() {
      this.hideModalBackdrop = true;
      this.hideModal();
      setTimeout(() => {
        this.$nextTick(() => {
          html2canvas(document.querySelector('#capture')).then((canvas) => {
            const link = document.createElement('a');
            link.download = 'capture.png';
            this.model.image = canvas.toDataURL();

            this.hideModalBackdrop = false;
            this.stepId = STEPS.CONFIRM_INFO;
            this.showModal();
          });
        });
      }, 100);
    },
    async onContinueHandler() {
      const isValid = await this.$refs.formRef.validate();
      if (!isValid) {
        this.ShowToast(
          i18n.t('Alert'),
          `${i18n.t('PleaseCompleteRequestedInformation')}.`,
          'error'
        );
        return;
      }
      this.stepId = STEPS.SCREEN_CAPTURE;
    },
    onCaptureScreenHandler() {
      this.captureScreen();
    },
    async onSendHandler() {
      const payload = {
        ...this.model,
        caseNumber: this.selectedRequestNumber,
      };
      const { data: saveResponse } = await _sendIncidentReport(payload).catch(
        () => {
          this.ShowToast('', i18n.t('FeedbackmessageError'), 'error');
        }
      );
      if (saveResponse) {
        this.ShowToast(
          i18n.t('Feedbacklabel'),
          i18n.t('FeedbackmessageSuccess'),
          'success'
        );
        this.hideModal();
      }
    },
    onHideHandler() {
      if (!this.isScreenCaptureStep) {
        this.model = {};
        this.stepId = STEPS.FILL_FORM;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/components/action-button/index.scss';
@import '@/scss/bootstrap/_colors.scss';

.custom-modal__extended {
  ::v-deep .modal-dialog {
    max-width: 700px !important;
  }
}
::v-deep .btn-highlight {
  border-color: map-get($colors, 'dark-blue-1') !important;
  color: map-get($colors, 'dark-blue-1') !important;
}
::v-deep .btn-blackout {
  border-color: map-get($colors, 'dark-blue-1') !important;
  background: map-get($colors, 'dark-blue-1') !important;
}
</style>
